import { useEffect } from 'react'
import { PostMessageParams } from '~/types'

import { useSharedState } from '@mangovoice/core'
import { Message } from '@mangovoice/types'

export interface PendingMessagesProps {
  threadId: number
}

export interface PendingMessage {
  uuid: string
  messageId?: number
  body: string
  messageType: 'text' | 'image' | 'file'
  status: 'sending' | 'failed'
  created_at: Date
  sender_extension_id?: number
  sender_user_id?: number
  direction: 'outbound' | 'inbound'
  originalSendParams?: PostMessageParams
}

interface PendingMessages {
  messages: PendingMessage[]
  /** Number of places using the current thread, used for cleaning up state */
  threadOpenCount: number
}

export const usePendingMessages = ({ threadId }: PendingMessagesProps) => {
  const [
    pendingMessages,
    setPendingMessages,
    pendingMessagesSource,
    removePendingMessagesSource,
  ] = useSharedState<PendingMessages>({
    key: threadId.toString(),
    namespace: 'thread-pending-messages',
    initialState: { messages: [], threadOpenCount: 0 },
  })

  const setPendingMessage = (message: PendingMessage) => {
    const index = pendingMessagesSource.messages.findIndex(
      (existingMessage) => existingMessage.uuid === message.uuid,
    )
    if (index === -1) {
      // Inserting new message
      setPendingMessages({
        ...pendingMessagesSource,
        messages: [message, ...pendingMessagesSource.messages],
      })
    } else {
      // Updating existing message
      const copy = [...pendingMessagesSource.messages]
      copy[index] = message
      setPendingMessages({
        ...pendingMessagesSource,
        messages: copy,
      })
    }
  }

  const removePendingMessages = (
    messageId: number | number[] | Message | Message[],
  ) => {
    // Flatten the type to just an array of numbers
    const messageIds = (Array.isArray(messageId) ? messageId : [messageId]).map(
      (item) => (typeof item === 'number' ? item : item.id),
    )
    const updatedMessages = pendingMessagesSource.messages.filter(
      (message) =>
        message.messageId === undefined ||
        !messageIds.includes(message.messageId),
    )
    // If no changes were made, don't update the state
    if (pendingMessagesSource.messages.length === updatedMessages.length) {
      return
    }
    setPendingMessages({
      ...pendingMessagesSource,
      messages: updatedMessages,
    })
  }

  const getPendingMessage = (uuid: string) => {
    return pendingMessagesSource.messages.find(
      (message) => message.uuid === uuid,
    )
  }

  const decrementThreadOpenCount = () => {
    if (pendingMessagesSource.threadOpenCount <= 1) {
      // No pending messages are left and we are dismissing the last usage of this threadId instance, so lets clean up the pending messages source
      removePendingMessagesSource()
    } else {
      setPendingMessages({
        ...pendingMessagesSource,
        threadOpenCount: pendingMessagesSource.threadOpenCount - 1,
      })
    }
  }

  useEffect(() => {
    // Increment the thread open count when the hook is mounted
    setPendingMessages({
      ...pendingMessagesSource,
      threadOpenCount: pendingMessagesSource.threadOpenCount + 1,
    })
    // Decrement the thread open count when the hook is unmounted
    return () => {
      decrementThreadOpenCount()
    }
  }, [])

  return {
    pendingMessages: pendingMessages.messages,
    getPendingMessage,
    removePendingMessages,
    setPendingMessage,
  }
}
